import { useEffect, useState } from 'react'

import { usePlatformConfig } from '../../context/PlatformConfigContext'
import { resolveClientUiImageModel } from '../../lib/clientUiImageModel'
import { wholePagePreviewAspectClass } from '../../lib/comicPageLayout'
import { formatPageGenerationSnapshotLabel } from '../../lib/comicPageGenerationSnapshot'
import { comicPageToLightboxItems, lightboxIndexForPageCandidate } from '../../lib/comicPageLightbox'
import { type ComicPanelAspectPreset } from '../../lib/comicProjectSettings'
import type { ComicPage } from '../../lib/comicTypes'
import { mediaDisplayUrl } from '../../lib/resolvedMediaImage'
import { ChromePrimaryButton, ChromeToolbarButton } from '../ChromeActions'
import { ImageLightbox } from '../ImageLightbox'
import { ThemeModal } from '../modal'
import { ThemedImage } from '../ThemedImage'
import { comicPanelRoundSm } from './comicPanelTokens'

type Props = {
  open: boolean
  angular: boolean
  panelAspect: ComicPanelAspectPreset
  page: ComicPage | null
  onClose: () => void
  onSelect: (candidateId: string) => void
  onEditCandidate?: (candidateId: string) => void
  /** 队列模式下：选定后关闭并继续下一页 */
  applyLabel?: string
}

function candidateThumbClass(angular: boolean, selected: boolean): string {
  return [
    'relative block overflow-hidden bg-surface-muted transition-[box-shadow]',
    comicPanelRoundSm(angular),
    selected
      ? 'shadow-[inset_0_0_0_2px_var(--accent)]'
      : 'shadow-[inset_0_0_0_1px_var(--line)] hover:shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--accent)_55%,var(--line))]',
  ].join(' ')
}

function candidatePreviewFrameClass(angular: boolean): string {
  return [
    'relative w-full overflow-hidden bg-surface-muted',
    comicPanelRoundSm(angular),
    'shadow-[inset_0_0_0_2px_var(--accent)]',
  ].join(' ')
}

export function ComicPageCandidatesModal({
  open,
  angular,
  panelAspect: _panelAspect,
  page,
  onClose,
  onSelect,
  onEditCandidate,
  applyLabel = '应用为整页',
}: Props) {
  const { clientUi } = usePlatformConfig()
  const candidates = page?.pageCandidates ?? []
  const selectedId = page?.selectedPageCandidateId ?? null
  const [focusCandidateId, setFocusCandidateId] = useState<string | null>(null)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const lightboxItems = comicPageToLightboxItems(page)
  const wholePageAspect = page ? wholePagePreviewAspectClass(page.layout) : wholePagePreviewAspectClass('1x1')

  const focusCandidate = candidates.find((c) => c.id === focusCandidateId) ?? null
  const focusApplied = focusCandidateId != null && focusCandidateId === selectedId
  const modelLabel = focusCandidate?.generation
    ? resolveClientUiImageModel(
        clientUi,
        focusCandidate.generation.modelId,
        focusCandidate.generation.platformId,
      )?.label
    : undefined
  const snapshotLabel = focusCandidate
    ? formatPageGenerationSnapshotLabel(focusCandidate.generation, modelLabel)
    : null

  const openPreview = (candidateId: string) => {
    setLightboxIndex(lightboxIndexForPageCandidate(page, candidateId))
  }

  useEffect(() => {
    if (!open) {
      setFocusCandidateId(null)
      setLightboxIndex(null)
      return
    }
    if (selectedId) {
      setFocusCandidateId(selectedId)
    } else if (candidates.length > 0) {
      setFocusCandidateId(candidates[0]!.id)
    }
  }, [open, page, selectedId, candidates])

  return (
    <>
      <ThemeModal
        open={open}
        onClose={onClose}
        title={page ? `${page.title} · 整页候选` : '整页候选'}
        angular={angular}
        size="lg"
        bodyClassName="px-4 py-4 sm:px-5 sm:py-5"
        footer={
          candidates.length > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="min-w-0 text-xs text-ink-soft">
                {focusCandidate
                  ? focusApplied
                    ? '当前整页定稿'
                    : `已选候选 #${candidates.findIndex((c) => c.id === focusCandidate.id) + 1}`
                  : '请从右侧网格选择一张候选'}
              </p>
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                {onEditCandidate && focusCandidateId ? (
                  <ChromeToolbarButton
                    type="button"
                    onClick={() => onEditCandidate(focusCandidateId)}
                  >
                    改图修字
                  </ChromeToolbarButton>
                ) : null}
                <ChromePrimaryButton
                  density="compact"
                  type="button"
                  disabled={!focusCandidateId || focusApplied}
                  onClick={() => {
                    if (focusCandidateId) onSelect(focusCandidateId)
                  }}
                >
                  {focusApplied ? '当前整页' : applyLabel}
                </ChromePrimaryButton>
              </div>
            </div>
          ) : undefined
        }
      >
        {candidates.length === 0 ? (
          <p className="text-sm text-ink-soft">暂无候选图</p>
        ) : (
          <section>
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-ink">选择整页成图</p>
            <div className="mt-2 flex h-[min(44vh,15.5rem)] items-stretch sm:h-[min(46vh,16.5rem)]">
            <div className="flex w-[10.25rem] shrink-0 flex-col justify-center sm:w-44">
              {focusCandidate?.imageUrl?.trim() ? (
                <button
                  type="button"
                  className={[candidatePreviewFrameClass(angular), wholePageAspect, 'max-h-44 w-full'].join(' ')}
                  onClick={() => openPreview(focusCandidate.id)}
                  aria-label="放大预览候选图"
                >
                  <ThemedImage
                    src={mediaDisplayUrl(focusCandidate.imageUrl)}
                    alt=""
                    angular={angular}
                    className="absolute inset-0 h-full w-full object-cover"
                    fallbackClassName="absolute inset-0 flex h-full w-full items-center justify-center bg-surface-muted text-xs text-ink-soft"
                  />
                  <span className="pointer-events-none absolute bottom-1 right-1 rounded-[var(--radius-sm)] bg-panel/90 px-1 py-px text-[9px] font-semibold text-ink-soft">
                    放大
                  </span>
                </button>
              ) : (
                <div
                  className={[
                    candidatePreviewFrameClass(angular),
                    wholePageAspect,
                    'flex max-h-44 w-full items-center justify-center px-1.5 text-center text-[11px] leading-snug text-ink-soft',
                  ].join(' ')}
                >
                  请从右侧
                  <br />
                  选择候选
                </div>
              )}
              {snapshotLabel ? (
                <p className="mt-1.5 line-clamp-2 text-center text-[11px] text-ink-soft" title={snapshotLabel}>
                  {snapshotLabel}
                </p>
              ) : focusApplied ? (
                <p className="mt-1.5 text-center text-[11px] text-ink-soft">当前整页定稿</p>
              ) : focusCandidate ? (
                <p className="mt-1.5 text-center text-[11px] text-ink-soft">
                  候选 #{candidates.findIndex((c) => c.id === focusCandidate.id) + 1}
                </p>
              ) : null}
            </div>

            <div
              role="separator"
              aria-orientation="vertical"
              className="shell-rail-section-divider mx-3 w-px shrink-0 self-stretch border-l border-line bg-transparent"
            />

            <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
              <div className="scroll-region h-full overflow-y-auto pe-0.5">
                <ul className="grid grid-cols-3 gap-1 sm:grid-cols-4">
                  {candidates.map((c, i) => {
                    const focused = c.id === focusCandidateId
                    const applied = c.id === selectedId
                    return (
                      <li key={c.id}>
                        <button
                          type="button"
                          className={[candidateThumbClass(angular, focused), wholePageAspect, 'w-full'].join(' ')}
                          onClick={() => setFocusCandidateId(c.id)}
                          onDoubleClick={(e) => {
                            e.preventDefault()
                            openPreview(c.id)
                          }}
                          aria-pressed={focused}
                        >
                          <img
                            src={mediaDisplayUrl(c.imageUrl)}
                            alt=""
                            className="h-full w-full object-cover"
                            draggable={false}
                            loading={i < 8 ? 'eager' : 'lazy'}
                            decoding="async"
                          />
                          {applied ? (
                            <span className="absolute left-0.5 top-0.5 rounded-[var(--radius-sm)] bg-accent px-1 py-px text-[8px] font-bold leading-none text-white">
                              定稿
                            </span>
                          ) : null}
                          <span className="absolute bottom-0.5 right-0.5 rounded-[var(--radius-sm)] bg-panel/90 px-1 py-px text-[8px] font-bold leading-none tabular-nums text-ink-soft">
                            {i + 1}
                          </span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              </div>
            </div>
          </div>
          </section>
        )}
      </ThemeModal>
      <ImageLightbox
        open={lightboxIndex != null && lightboxItems.length > 0}
        items={lightboxItems}
        index={lightboxIndex ?? 0}
        angular={angular}
        onIndexChange={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />
    </>
  )
}
